<script setup lang="ts">
import type { SiteColumnsRatio } from '../../types/site'

/**
 * Grid shell of the `columns` block, shared by the public ColumnsBlock and an
 * editor's per-column drop zones: the consumer fills each column through the
 * scoped `column` slot (index in `i`).
 *
 * The ratio becomes a `--site-cols` custom property so only static Tailwind
 * arbitrary-property classes are needed (no runtime class generation).
 */
const props = withDefaults(defineProps<{
  ratio?: SiteColumnsRatio | string
  gap?: 's' | 'm' | 'l' | string
  verticalAlign?: 'top' | 'center' | 'stretch' | string
  stackOnMobile?: boolean
}>(), {
  ratio: '1-1',
  gap: 'm',
  verticalAlign: 'top',
  stackOnMobile: true
})

const RATIOS = ['1-1', '1-2', '2-1', '1-1-1', '1-1-1-1']

const segments = computed(() => (RATIOS.includes(props.ratio) ? props.ratio : '1-1').split('-'))
const count = computed(() => segments.value.length)
const template = computed(() => segments.value.map(s => `${s}fr`).join(' '))

const gapClass = computed(() => ({ s: 'gap-3', m: 'gap-6', l: 'gap-10' }[props.gap] ?? 'gap-6'))
const alignClass = computed(() => ({
  top: 'items-start',
  center: 'items-center',
  stretch: 'items-stretch'
}[props.verticalAlign] ?? 'items-start'))

defineExpose({ count })
</script>

<template>
  <div
    class="grid"
    :class="[
      gapClass,
      alignClass,
      stackOnMobile ? 'md:[grid-template-columns:var(--site-cols)]' : '[grid-template-columns:var(--site-cols)]'
    ]"
    :style="{ '--site-cols': template }"
  >
    <div
      v-for="i in count"
      :key="i - 1"
      class="min-w-0"
    >
      <slot
        name="column"
        :i="i - 1"
      />
    </div>
  </div>
</template>
